import {flatten, intersection, isEqual, isObject, some, transform, uniqWith} from 'lodash'
import produce from 'immer'
import {LibraryItem} from '../models/types'
import {UserPermissions} from 'powtoon-services/current-user'
import {isAvatarItem} from 'powtoon-document'
import {
  DocItem,
  FamiliesLicensedManifestItem,
  LicensedManifestItem,
  LicenseLevel,
  Manifest,
  Powtoon,
  Slide,
  SoundPackagesLicensedManifestItem,
  TextTrackSprite,
  TrackItem,
  TrackSprite,
  ManifestItemOptions
} from 'powtoon-versioning/src/currentSchema'
import {LICENSED_FEATURE_IDS} from './licensedFeature'
import {UserCurrentPlan} from 'powtoon-services/current-user/types'

export enum LICENSE_LEVEL {
  Free = 'Free',
  Professional = 'Professional',
  ProfessionalPlus = 'ProfessionalPlus',
  Agency = 'Agency',
  FreeGen1 = 'Free-Gen1',
  PremiumGen1 = 'Premium-Gen1',
  EduGen1 = 'Edu-Gen1',
  AdvancedGen1 = 'Advanced-Gen1'
}

export enum FreeLicenseCategory {
  Free = 'Free',
  FreeEdu = 'FreeEdu',
  FreeGen1 = 'FreeGen1',
  FreeEduGen1 = 'FreeEduGen1'
}

export const LICENSE_PLAN_FREE = 'Free'
export const LICENSE_PLAN_EDU = 'Edu'
export const LICENSE_PLAN_PRO = 'Pro'
export const LICENSE_PLAN_PRO_PLUS = 'ProPlus'
export const LICENSE_PLAN_AGENCY = 'Agency'
export const LICENSE_PLAN_STARTER = 'Starter'

export const LICENSE_PLAN_FREE_GEN1 = 'Free-Gen1'
export const LICENSE_PLAN_LITE_GEN1 = 'Lite-Gen1'
export const LICENSE_PLAN_PREMIUM_GEN1 = 'Pro-Gen1'
export const LICENSE_PLAN_EDU_GEN1 = 'Edu-Gen1'
export const LICENSE_PLAN_ADVANCED_GEN1 = 'Agency-Gen1'

export const OUTRO_TYPE_FULL = 'full'
export const OUTRO_TYPE_SILENT = 'silent'
export const OUTRO_TYPE_STAMP = 'stamp'
export const OUTRO_TYPE_NONE = 'none'

export const WATERMARK_TYPE_REGULAR = 'regular'
export const WATERMARK_TYPE_AI = 'regular-ai'
export const WATERMARK_TYPE_EDU = 'edu'
export const WATERMARK_TYPE_NONE = 'none'

export const ASPECT_RATIO_SQUARE = 'square'
export const ENABLE_SQUARE_OUTRO = 'Show'

export const LICENSE_CATEGORY_FREE_GEN1 = 'FreeGen1'
export const LICENSE_CATEGORY_FREE_EDU_GEN1 = 'FreeEduGen1'
export const LICENSE_CATEGORY_LITE_GEN1 = 'LiteGen1'
export const LICENSE_CATEGORY_PREMIUM_GEN1 = 'ProGen1'
export const LICENSE_CATEGORY_EDU_GEN1 = 'EduGen1'
export const LICENSE_CATEGORY_MIGRATION_EDU_GEN1 = 'FreeEdu'
export const LICENSE_CATEGORY_AGENCY_PROMO_GEN1 = 'AgencyPromoGen1'
export const LICENSE_CATEGORY_ADVANCED_GEN1 = 'AgencyGen1'

export const LICENSE_LEVEL_WEIGHT: {[key in LicenseLevel]: number} = {
  [LICENSE_LEVEL.Free]: 0,
  [LICENSE_LEVEL.FreeGen1]: 0,
  [LICENSE_LEVEL.Professional]: 1,
  [LICENSE_LEVEL.ProfessionalPlus]: 2,
  [LICENSE_LEVEL.Agency]: 3,
  [LICENSE_LEVEL.EduGen1]: 4,
  [LICENSE_LEVEL.PremiumGen1]: 5,
  [LICENSE_LEVEL.AdvancedGen1]: 6
}

export const getMaxRequiredLicenseLevelName = (licenseLevel: LicenseLevel): string =>
  ({
    [LICENSE_LEVEL.Free]: 'Free',
    [LICENSE_LEVEL.FreeGen1]: 'Free',
    [LICENSE_LEVEL.Professional]: 'PRO',
    [LICENSE_LEVEL.ProfessionalPlus]: 'PRO+',
    [LICENSE_LEVEL.Agency]: 'AGENCY',
    [LICENSE_LEVEL.PremiumGen1]: 'PREMIUM',
    [LICENSE_LEVEL.AdvancedGen1]: 'ADVANCED',
    [LICENSE_LEVEL.EduGen1]: 'EDU'
  })[licenseLevel] || 'PRO'

function getLicensePlan(instance: {licensePlan: string} | string): string {
  return isObject(instance) ? instance.licensePlan : instance
}

export function isEdu(instance: {licensePlan: string} | string) {
  return getLicensePlan(instance) === LICENSE_PLAN_EDU
}

export function isAgency(instance: {licensePlan: string} | string) {
  return getLicensePlan(instance) === LICENSE_PLAN_AGENCY
}

export function isAdvancedGenOne(instance: {licensePlan: string} | string) {
  return getLicensePlan(instance) === LICENSE_PLAN_ADVANCED_GEN1
}

export function isPremiumGenOne(instance: {licensePlan: string} | string) {
  return getLicensePlan(instance) === LICENSE_PLAN_PREMIUM_GEN1
}

function getLicenseLevel(instance: {licenseLevel: string} | string) {
  return isObject(instance) ? instance.licenseLevel : instance
}

export function isFreeGenZero(instance: {licenseLevel: string} | string) {
  return getLicenseLevel(instance) === LICENSE_LEVEL.Free
}

export function isFreeGenOne(instance: {licenseLevel: string} | string) {
  return getLicenseLevel(instance) === LICENSE_LEVEL.FreeGen1
}

export function isFree<T extends {licenseLevel: string}>(instance: T | string) {
  return isFreeGenZero(instance) || isFreeGenOne(instance)
}

export function isFreeUser(instance: {licenseCategory: string} | string | undefined) {
  const licenseCategory = typeof instance === 'object' ? instance.licenseCategory : instance
  return licenseCategory ? Object.values(FreeLicenseCategory).includes(licenseCategory as FreeLicenseCategory) : false
}

export function isLiteUser(instance: {licenseCategory: string} | string | undefined) {
  const licenseCategory = typeof instance === 'object' ? instance.licenseCategory : instance
  return licenseCategory ? licenseCategory === LICENSE_CATEGORY_LITE_GEN1 : false
}

export function isProfessional(instance: {licenseLevel: string, licenseCategory: string} | string) {
  const licenseCategory = typeof instance === 'object' ? instance.licenseCategory : instance
  return getLicenseLevel(instance) === LICENSE_LEVEL.Professional || licenseCategory === LICENSE_CATEGORY_PREMIUM_GEN1
}

export function isProfessionalPlus(instance: {licenseLevel: string} | string) {
  return getLicenseLevel(instance) === LICENSE_LEVEL.ProfessionalPlus
}

export function isAgencyLicenseLevel(instance: {licenseLevel: string} | string) {
  return getLicenseLevel(instance) === LICENSE_LEVEL.Agency
}

export function isEduGenOne(instance: {licenseLevel: string} | string) {
  return getLicenseLevel(instance) === LICENSE_LEVEL.EduGen1
}

export function isPremiumGenOneLevel(instance: {licenseLevel: string} | string) {
  return getLicenseLevel(instance) === LICENSE_LEVEL.PremiumGen1
}

export function isAdvancedGenOneLevel(instance: {licenseLevel: string} | string) {
  return getLicenseLevel(instance) === LICENSE_LEVEL.AdvancedGen1
}

export function isTopTierPlan(licensePlan: string) {
  return licensePlan === LICENSE_PLAN_AGENCY || licensePlan === LICENSE_PLAN_ADVANCED_GEN1
}

export function isBilledMonthly(planData: UserCurrentPlan) {
  return 'intervalLength' in planData && planData.intervalLength === 1 && planData.intervalUnit === 'months'
}

function getSongIsLockedInfo(
  instanceRequiredLicenseLevel: LicenseLevel,
  instancePackages: string[],
  {soundPackages: allowedPackages}: {soundPackages: string[]}
) {

  /* istanbul ignore if  */
  if (window?.webvars?.is_enterprise) {
    /* istanbul ignore next */
    return false
  }

  const instanceHasAllowedPackages = intersection(flatten(instancePackages), allowedPackages).length > 0
  return !instanceHasAllowedPackages && !isFree(instanceRequiredLicenseLevel)
}

export const isLicensedItem = (item: any): item is LicensedManifestItem => !!item?.licenseLevel

export const isFamiliesLicensedManifestItem =
  (item: any): item is FamiliesLicensedManifestItem => Array.isArray(item?.families) && isLicensedItem(item)

export const isSoundPackagesLicensedManifestItem =
  (item: any): item is SoundPackagesLicensedManifestItem => Array.isArray(item?.soundPackages) && isLicensedItem(item)

export const isDocItem = (item: any): item is DocItem => !!item?.contentItemId

export const getSongWithLockInfo = (song: LibraryItem, permissions: UserPermissions) => (
  isLicensedItem(song) ?
    {...song, isLocked: getSongIsLockedInfo(song.licenseLevel, song.soundPackages, permissions)} :
    song
)

type SoundPackagesBasedLicensedManifestItemWithLockInfo =
  SoundPackagesLicensedManifestItem & {isLocked: boolean}

type FamilyBasedLicensedManifestItemWithLockInfo =
  FamiliesLicensedManifestItem & {isLocked: boolean}

type ManifestObjectsWithLocks = Record<
  string,
  SoundPackagesBasedLicensedManifestItemWithLockInfo | FamilyBasedLicensedManifestItemWithLockInfo
>

export const getManifestObjectWithLockInfo = <T extends ManifestItemOptions>(
  manifestObject: T,
  permissions: UserPermissions
): T & {isLocked?: boolean} => {
  if (isSoundPackagesLicensedManifestItem(manifestObject)) {
    return ({
      ...manifestObject,
      isLocked: getSongIsLockedInfo(manifestObject.licenseLevel, manifestObject.soundPackages, permissions)
    })
  }
  if (isFamiliesLicensedManifestItem(manifestObject)) {
    return ({
      ...manifestObject,
      isLocked: getInstanceIsLockedInfo(manifestObject.licenseLevel, manifestObject.families, permissions)
    })
  }
  return manifestObject
}

export function createGetManifestObjectsWithLockInfo() {

  let prevManifest: Manifest | null = null
  let prevPermissions: UserPermissions | null = null
  let storedManifestObjectsWithLocks: ManifestObjectsWithLocks | null = null

  return (manifest: Manifest, permissions: UserPermissions): ManifestObjectsWithLocks => {
    if (prevManifest === manifest && prevPermissions === permissions && storedManifestObjectsWithLocks) {
      return storedManifestObjectsWithLocks
    }

    prevManifest = manifest
    prevPermissions = permissions

    return storedManifestObjectsWithLocks = transform({
      ...manifest.assets, ...manifest.images, ...manifest.videos, ...manifest.sounds, ...manifest.fonts
    }, (manifestObjectsWithLocks, manifestObject, assetId) => {
      manifestObjectsWithLocks[assetId] = manifestObject && getManifestObjectWithLockInfo(manifestObject, permissions)
    }, {} as Record<string, any>)
  }
}

export const getManifestObjectsWithLockInfo = createGetManifestObjectsWithLockInfo()

export function getFamiliesWithLockInfo(families: LibraryItem[], permissions: UserPermissions) {
  return families.map(family => ({
    ...family,
    isLocked: getInstanceIsLockedInfo(family.licenseLevel, [family.id], permissions)
  }))
}

function getHasAllowedFamilies(families: string[], allowedFamilies: string[]) {
  return intersection(families, allowedFamilies).length > 0
}

function getIsPlainFamiliesArray(instanceFamilies: string[] | string[][]): instanceFamilies is string[] {
  return !some(instanceFamilies, item => Array.isArray(item))
}

export function getInstanceIsLockedInfo(
  instanceRequiredLicenseLevel: LicenseLevel,
  instanceFamilies: string[] | string[][],
  {families: allowedFamilies = []}: UserPermissions
) {


  /* istanbul ignore if  */
  if (window?.webvars?.is_enterprise) {
    /* istanbul ignore next */
    return false
  }

  if (isFree(instanceRequiredLicenseLevel)) {
    return false
  }
  const isPlainFamiliesArray = getIsPlainFamiliesArray(instanceFamilies)

  if (isPlainFamiliesArray) {
    return !getHasAllowedFamilies(instanceFamilies, allowedFamilies)
  }

  return some(instanceFamilies, item => !getHasAllowedFamilies(item, allowedFamilies))
}

export function getTracksWithLocks(tracks: Slide['tracks'], manifestObjectsWithLockInfo: ManifestObjectsWithLocks) {
  return tracks.map(track => getTrackWithLocks(track, manifestObjectsWithLockInfo))
}

export function getTrackWithLocks(
  track: TrackItem,
  manifestObjectsWithLockInfo: ManifestObjectsWithLocks,
  slideSceneId: string | null = null
) {
  if (!track) {
    return track
  }
  return {
    ...track,
    sprites: track.sprites.map(sprite => getSpriteWithLocks(sprite, manifestObjectsWithLockInfo, slideSceneId))
  }
}

type SpriteWithLocks = (TrackSprite | TextTrackSprite) & {
  isLocked: boolean,
  licenseLevel: LicenseLevel
}

export function getSpriteWithLocks(
  sprite: TrackSprite | TextTrackSprite,
  manifestObjectsWithLockInfo: ManifestObjectsWithLocks,
  slideSceneId: string | null
): SpriteWithLocks {

  const sceneManifestItem = contentItemIdLockedManifestItem(slideSceneId, manifestObjectsWithLockInfo)

  return produce(sprite, (draft: SpriteWithLocks) => {

    let spriteIsLocked = false
    let spriteLicenseLevel: LicenseLevel = LICENSE_LEVEL.Free

    forEachLicensedItem(draft, (licensedItem) => {
      const {isLocked, maxRequiredLicenseLevel} = getObjectLicensingSummary(licensedItem, manifestObjectsWithLockInfo)

      // TODO (maybe this method shouldnt be here at all?)
      // some magic to support current UI logic:
      // if slide is created from scene - all scene tracks shouldnt have watermarks on stage
      const itemLockedWOLicenseInheritance =
        isLocked &&
        (!licensedItem?.licenseSource || licensedItem?.licenseSource !== slideSceneId)

      // at the same time if inner content of the track has higher content - watermark should be displayed
      // e.g. image/video inside image holder or lipsync audioTrack
      const innerItemsOvercomingInheritLicense =
        sceneManifestItem &&
        licensedItem?.licenseSource === slideSceneId &&
        sceneManifestItem.licenseLevel !== maxRequiredLicenseLevel

      if (itemLockedWOLicenseInheritance || innerItemsOvercomingInheritLicense) {
        licensedItem.isLocked = isLocked
        licensedItem.licenseLevel = maxRequiredLicenseLevel
        spriteLicenseLevel = getMorePriorLicenseLevel(spriteLicenseLevel, maxRequiredLicenseLevel)
        spriteIsLocked = spriteIsLocked || isLocked
      }

    })

    if (spriteIsLocked || spriteLicenseLevel) {
      draft.isLocked = spriteIsLocked
      draft.licenseLevel = spriteLicenseLevel
    }
  }) as SpriteWithLocks

}

export const getMorePriorLicenseLevel = (
  firstLicenseLevel: LicenseLevel | null,
  secondLicenseLevel: LicenseLevel | null
): LicenseLevel => {

  if (!firstLicenseLevel || !secondLicenseLevel) {
    return firstLicenseLevel || secondLicenseLevel || LICENSE_LEVEL.Free
  }

  return (LICENSE_LEVEL_WEIGHT[secondLicenseLevel] || -1) > (LICENSE_LEVEL_WEIGHT[firstLicenseLevel] || -1) ?
    secondLicenseLevel :
    firstLicenseLevel
}

export const slideRequiredLicenseLevelStage = (slide: Slide, assetsWithLocks: ManifestObjectsWithLocks) => {
  const {sceneId, camera} = slide
  const {maxRequiredLicenseLevel, isLocked} = getObjectLicensingSummary({sceneId, camera}, assetsWithLocks)

  return isLocked ? maxRequiredLicenseLevel : null
}

export const slideRequiredLicenseLevelBackground = (slide: Slide, assetsWithLocks: ManifestObjectsWithLocks) =>
  isDocItem(slide.background) &&
  contentItemIdLockedManifestItem(slide.background.contentItemId, assetsWithLocks)?.licenseLevel

const contentItemIdLockedManifestItem = (
  contentItemId: string | null,
  assetsWithLocks: ManifestObjectsWithLocks
): ManifestObjectsWithLocks[string] | null => {

  const manifestItem = contentItemId ? assetsWithLocks[contentItemId] : null

  if (isLicensedItem(manifestItem) && manifestItem.isLocked &&
    manifestItem.licenseLevel && !isFree(manifestItem)) {
    return manifestItem
  }

  return null
}


function getObjectLicensingSummary(
  object: any, manifestObjectsWithLocks: ManifestObjectsWithLocks, requirePackagesSummary: boolean = false
) {

  let maxRequiredLicenseLevel: LicenseLevel = LICENSE_LEVEL.Free
  let lockItems: string[] = []

  const lockedPackagesSummary: {
    families: (string[] | string[][])[],
    soundPackages: string[][]
  } = {
    families: [],
    soundPackages: []
  }

  forEachLicensedItem(object, (item, licensedRefs) => {

    licensedRefs?.forEach(licensedRef => {

      const lockedManifestItem = contentItemIdLockedManifestItem(licensedRef, manifestObjectsWithLocks)

      if (!lockedManifestItem) {
        return
      }

      // TODO maybe we should assign ID for EACH DocItem - and work only with ids here...
      lockItems.push(item.id || item.contentItemId || item.featureId)
      maxRequiredLicenseLevel = getMorePriorLicenseLevel(lockedManifestItem.licenseLevel, maxRequiredLicenseLevel)

      if (requirePackagesSummary) {
        if (isSoundPackagesLicensedManifestItem(lockedManifestItem)) {
          lockedPackagesSummary.soundPackages.push(lockedManifestItem.soundPackages)
        } else {
          lockedPackagesSummary.families.push(lockedManifestItem.families)
        }
      }
    })
  })

  lockItems = uniqWith(lockItems, isEqual)
  if (requirePackagesSummary) {
    lockedPackagesSummary.families = uniqWith(lockedPackagesSummary.families, isEqual)
    lockedPackagesSummary.soundPackages = uniqWith(lockedPackagesSummary.soundPackages, isEqual)
  }

  return ({maxRequiredLicenseLevel, lockItems, isLocked: !!lockItems.length, lockedPackagesSummary})

}

export const getPowtoonLicenseInfo = (
  {slides, audioTracks}: Powtoon,
  manifestObjectsWithLocks: ManifestObjectsWithLocks,
  requirePackagesSummary: boolean = false
) => {
  return getObjectLicensingSummary(
    {slides, audioTracks},
    manifestObjectsWithLocks,
    requirePackagesSummary
  )
}

function getLicensingRefs(item: any): string[] | null {

  if (!item) {
    return null
  }

  const {licenseSource, contentItemId, featureId, sceneId} = item

  if (licenseSource) {
    return [licenseSource]
  }

  const licensedRefs = [
    contentItemId, featureId, sceneId
  ].filter(Boolean)

  return licensedRefs.length ? licensedRefs : null
}

function forEachLicensedItem(item: any, cb: (item: any, licensedRefs: string[] | null) => void) {
  if (Array.isArray(item)) {
    item.forEach(i => {
      forEachLicensedItem(i, cb)
    })
  }
  else if (isObject(item)) {
    const licensedRefs = getLicensingRefs(item)

    if (licensedRefs) {
      cb(item, licensedRefs)
    }

    Object.values(item).forEach(i => {
      forEachLicensedItem(i, cb)
    })
  }
}

export const getAudioTrackLicensedFeature = (sprite: TrackSprite) => {
  if (isAvatarItem(sprite)) {
    return null
  }
  return LICENSED_FEATURE_IDS.LIPSYNC
}

// Enterprise
export const EC_LICENSE_TRIAL = 'Free'
export const EC_LICENSE_TRIAL_GEN_1 = 'FreeGen1'
export const EC_LICENSE_CREATOR = 'EC_Creator'
export const EC_LICENSE_EDITOR = 'EC_Editor'
export const EC_LICENSE_VIEWER = 'EC_Viewer'

export type ECLicenseType = 'Free' | 'EC_Creator' | 'EC_Editor' | 'EC_Viewer' | 'FreeGen1'

export const EC_LICENSE_DISPLAY_NAMES: Record<ECLicenseType, string> = {
  [EC_LICENSE_TRIAL]: 'Trial',
  [EC_LICENSE_TRIAL_GEN_1]: 'Trial',
  [EC_LICENSE_VIEWER]: 'Viewer',
  [EC_LICENSE_CREATOR]: 'Creator',
  [EC_LICENSE_EDITOR]: 'Limited Creator'
}

export const EC_LICENSE_WEIGHT: {[key in ECLicenseType]: number} = {
  [EC_LICENSE_VIEWER]: 0,
  [EC_LICENSE_TRIAL]: 0,
  [EC_LICENSE_TRIAL_GEN_1]: 0,
  [EC_LICENSE_EDITOR]: 1,
  [EC_LICENSE_CREATOR]: 2
}
